import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

const breadcrumbItems = [
  { label: '首页', to: '/' },
  { label: '关于我们' },
];

const milestones = [
  { year: '2018', title: '平台创立', description: '黑料网正式上线，致力于为用户提供最新、最全的资讯内容。' },
  { year: '2019', title: '用户突破百万', description: '凭借优质内容和良好口碑，注册用户数突破100万大关。' },
  { year: '2020', title: '域名体系建立', description: '建立多域名备份体系，确保用户在任何网络环境下都能稳定访问。' },
  { year: '2021', title: '全球CDN部署', description: '完成全球CDN节点部署，为海外用户提供高速流畅的访问体验。' },
  { year: '2022', title: '内容升级', description: '全面升级内容质量，引入专业编辑团队，提升资讯的深度和广度。' },
  { year: '2023', title: '安全加固', description: '全面升级安全防护体系，采用先进加密技术保护用户隐私和数据安全。' },
  { year: '2024', title: '用户突破千万', description: '累计服务用户突破1000万，成为中国领先的综合资讯平台之一。' },
  { year: '2025', title: '智能推荐', description: '上线AI智能推荐系统，为用户精准推送感兴趣的资讯内容。' },
  { year: '2026', title: '全新升级', description: '全面改版升级，优化用户体验，推出永久域名导航服务。' },
];

const testimonials = [
  {
    name: '张先生',
    role: '资深用户 · 使用5年',
    content: '从2019年开始使用黑料网，最让我放心的是域名导航功能。以前经常因为域名失效找不到入口，现在有了这个永久域名页面，再也不用担心了。收藏一个页面就能随时找到最新入口，真心推荐！',
  },
  {
    name: '李女士',
    role: 'VIP用户 · 使用3年',
    content: '黑料网的资讯更新速度真的很快，很多热点事件都是在这里第一时间看到的。域名导航页面也很实用，多个备用地址随时可用，访问从没断过。客服响应也很快，遇到问题基本秒回。',
  },
  {
    name: '王先生',
    role: '企业用户 · 使用4年',
    content: '作为一名经常需要追踪行业动态的内容从业者，黑料网是我每天必看的平台。信息全面、更新及时，而且域名导航让我在出差途中也能稳定访问。平台的稳定性和内容丰富度都值得称赞。',
  },
  {
    name: '赵女士',
    role: '活跃用户 · 使用2年',
    content: '之前总是找不到黑料网的最新域名，每次都要到处搜索，还经常找到假的钓鱼网站。自从收藏了永久域名页面，一键直达官方入口，安全又方便。资讯内容丰富多样，每天刷一刷已经成为习惯了。',
  },
];

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setSubmitStatus('success');
      setContactForm({ name: '', email: '', message: '' });
      return;
    }

    try {
      const formData = new FormData(form);
      const submitAddr = 'https://readdy.ai/api/form/d9qshnvnahb7382oveug';
      const response = await fetch(submitAddr, {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setSubmitStatus('success');
        setContactForm({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
        setSubmitStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查您的网络连接后重试');
      setSubmitStatus('error');
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-800 text-xs font-medium rounded-full mb-4">
                关于我们
              </span>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                关于<strong className="text-primary-600">黑料网永久域名</strong>
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                黑料网永久域名是黑料网的官方域名导航平台，致力于为用户提供最新、最全、最稳定的域名地址汇总服务。我们深知域名访问的稳定性和可靠性对于用户的重要性，因此建立了完善的多域名备份和实时更新机制。
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 mb-16">
              <div>
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-4">品牌故事</h2>
                <div className="space-y-3 text-sm text-foreground-700 leading-relaxed">
                  <p>
                    黑料网自2018年创立以来，始终秉持"为用户提供最优质的资讯服务"的核心理念，经过多年的发展，已经成为国内领先的综合资讯平台之一。
                  </p>
                  <p>
                    随着平台不断发展壮大，我们深刻认识到域名稳定性的重要性。在复杂的网络环境中，单一域名可能面临各种不可预见的访问障碍。为了解决这一用户痛点，我们建立了完善的<strong>多域名备份体系</strong>，并推出了永久域名导航页面。
                  </p>
                  <p>
                    我们的域名管理团队实行7×24小时值班制度，持续监控所有域名的可用性。一旦发现某个域名出现异常，团队会在第一时间启动备用域名并同步更新导航页面，确保用户的访问体验不受影响。
                  </p>
                  <p>
                    目前，黑料网已累计服务超过<strong>1000万用户</strong>，日均活跃用户超过<strong>200万</strong>。这些数字的背后，是每一位用户对我们平台的信任和支持，也是我们不断优化和进步的动力源泉。
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-4">核心价值</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { icon: 'ri-shield-check-line', title: '安全可靠', desc: '所有域名经过严格验证，杜绝假冒钓鱼风险' },
                    { icon: 'ri-refresh-line', title: '实时更新', desc: '域名状态24h监控，失效域名秒级替换' },
                    { icon: 'ri-global-line', title: '全球覆盖', desc: '部署全球CDN节点，任何地区都能流畅访问' },
                    { icon: 'ri-lock-line', title: '隐私保护', desc: '先进加密技术，严格保护用户访问隐私' },
                  ].map((item, i) => (
                    <div key={i} className="p-4 bg-background-100 rounded-lg border border-background-200/70">
                      <div className="w-8 h-8 flex items-center justify-center bg-accent-100 rounded-md mb-2">
                        <i className={`${item.icon} text-sm text-accent-600`}></i>
                      </div>
                      <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">{item.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 bg-accent-100 text-accent-800 text-xs font-medium rounded-full mb-4">
                发展历程
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                品牌<strong className="text-primary-600">发展历程</strong>
              </h2>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300/60 transform md:-translate-x-px"></div>
              <div className="space-y-6 md:space-y-8">
                {milestones.map((item, index) => (
                  <div
                    key={index}
                    className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-primary-500 rounded-full transform -translate-x-1/2 mt-1.5 z-10"></div>
                    <div className={`md:w-1/2 pl-10 md:pl-0 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                      <div className="bg-background-50 rounded-lg p-4 md:p-5 border border-background-200/70">
                        <span className="text-xs font-bold text-primary-600">{item.year}</span>
                        <h3 className="text-sm font-heading font-semibold text-foreground-900 mt-1 mb-1">{item.title}</h3>
                        <p className="text-xs text-foreground-500 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                    <div className="hidden md:block md:w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-800 text-xs font-medium rounded-full mb-4">
                用户口碑
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                用户<strong className="text-primary-600">真实评价</strong>
              </h2>
              <p className="text-sm text-foreground-600 mt-3">以下评价来自真实用户，代表了广大用户对黑料网的支持和认可</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-background-100 rounded-lg p-5 md:p-6 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-sm text-primary-500"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3 pt-3 border-t border-background-200/70">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary-600">{t.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-800 text-xs font-medium rounded-full mb-4">
                联系我们
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                联系<strong className="text-primary-600">我们</strong>
              </h2>
              <p className="text-sm text-foreground-600 mt-3">
                如果您有任何建议、意见或问题，欢迎通过以下方式联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-10">
              <div className="bg-background-50 rounded-lg p-4 text-center border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg mx-auto mb-3">
                  <i className="ri-mail-line text-lg text-primary-600"></i>
                </div>
                <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                <p className="text-xs text-foreground-500">support@heiliaowang.com</p>
              </div>
              <div className="bg-background-50 rounded-lg p-4 text-center border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg mx-auto mb-3">
                  <i className="ri-time-line text-lg text-accent-600"></i>
                </div>
                <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">服务时间</h3>
                <p className="text-xs text-foreground-500">7×24小时全天候服务</p>
              </div>
              <div className="bg-background-50 rounded-lg p-4 text-center border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-lg mx-auto mb-3">
                  <i className="ri-chat-smile-2-line text-lg text-secondary-600"></i>
                </div>
                <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">在线客服</h3>
                <p className="text-xs text-foreground-500">工作日 9:00 - 21:00</p>
              </div>
            </div>

            <div className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/70">
              <h3 className="text-base font-heading font-semibold text-foreground-900 mb-4">发送消息</h3>

              {submitStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 flex items-center justify-center bg-accent-100 rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-2xl text-accent-600"></i>
                  </div>
                  <p className="text-base font-heading font-semibold text-foreground-900 mb-1">消息已发送！</p>
                  <p className="text-sm text-foreground-500">感谢您的留言，我们会尽快回复您。</p>
                  <button
                    onClick={() => setSubmitStatus('idle')}
                    className="mt-4 px-5 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    发送新消息
                  </button>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} data-readdy-form="">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="visually-hidden"
                  />
                  <div className="flex flex-col gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        您的姓名
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        电子邮箱
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的电子邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容（不超过500字）"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">{contactForm.message.length}/500</p>
                    </div>
                    {formError && (
                      <p className="text-sm text-red-500">{formError}</p>
                    )}
                    <button
                      type="submit"
                      disabled={submitStatus === 'loading'}
                      className="w-full py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {submitStatus === 'loading' ? '发送中...' : '发送消息'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'AboutPage',
              name: '关于黑料网永久域名',
              description: '黑料网永久域名是黑料网的官方域名导航平台，致力于为用户提供最新、最全、最稳定的域名地址汇总服务。',
              about: {
                '@type': 'Organization',
                name: '黑料网永久域名',
                description: '黑料网永久域名官方入口，提供最新最全的域名地址汇总。',
                foundingDate: '2018',
                contactPoint: {
                  '@type': 'ContactPoint',
                  contactType: 'customer service',
                  email: 'support@heiliaowang.com',
                  availableLanguage: 'Chinese',
                },
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}